import { useCallback, useEffect, useState } from 'react';
import type { ApiEnvelope } from '../types';

// In dev, Vite proxies /api and /health to the Worker (see vite.config.ts),
// so a relative base works everywhere. Override with VITE_API_BASE_URL for a
// cross-origin production deployment.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch a JSON `ApiEnvelope<T>` from the Supply Chain OS API and unwrap `data`.
 * Re-runs whenever `path` changes.
 */
export function useAPI<T>(path: string): UseApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!path) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    fetch(`${BASE_URL}${path}`, { signal: controller.signal })
      .then(async (res) => {
        const body = (await res.json()) as ApiEnvelope<T>;
        if (!res.ok || !body.ok) {
          throw new Error(body.error || `Request failed (${res.status})`);
        }
        if (active) setData(body.data);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (active) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [path, nonce]);

  return { data, loading, error, refetch };
}
